import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getProfile, updateProfile, changePassword } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Upload, User, Building, FileText, CreditCard, Lock } from "lucide-react";
import { isUsClinicalHost } from "../lib/usClinicalHost";
import { useAuroraTour } from "../hooks/useAuroraTour";
import { TOUR_CONFIG } from "../lib/auroraTourSteps";
import { clearAllAuroraTours } from "../lib/auroraTour";
import DemoTooltip from "../components/DemoTooltip";

export default function Settings() {
  const isUsHost = isUsClinicalHost();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [profile, setProfile] = useState({
    nombre_completo: "",
    especialidad: "Geriatría",
    numero_colegiado: "",
    clinica_nombre: "",
    clinica_ciudad: "",
    clinica_direccion: "",
    clinica_telefono: "",
    clinica_email: "",
    logo_base64: null,
    firma_base64: null,
    encabezado_texto: "",
    pie_pagina_texto: ""
  });

  const isDemoUser = user?.email === "demo@aurorageriatrica.com";

  const fetchProfile = useCallback(async () => {
    try {
      const response = await getProfile();
      if (user?.email === "demo@aurorageriatrica.com") {
        setProfile({
          nombre_completo: "Dr. Aurora Demo",
          numero_colegiado: "COL-2025-GTM",
          especialidad: "Geriatría",
          clinica_nombre: "Clínica Geriátrica Aurora",
          clinica_ciudad: "Guatemala",
          clinica_direccion: "7a Avenida 7-07, Zona 9, Ciudad de Guatemala",
          clinica_telefono: "+502 2345-6789",
          clinica_email: "demo@aurorageriatrica.com",
          frase1: "Especialistas en atención integral del adulto mayor",
          frase2: "Consultas presenciales y seguimiento clínico especializado",
          logo_base64: null,
          firma_base64: null,
        });
        setLoading(false);
        return;
      }
      setProfile({
        nombre_completo: response.data.nombre_completo || "",
        especialidad: response.data.especialidad || "Geriatría",
        numero_colegiado: response.data.numero_colegiado || "",
        clinica_nombre: response.data.clinica_nombre || "",
        clinica_ciudad: response.data.clinica_ciudad || "",
        clinica_direccion: response.data.clinica_direccion || "",
        clinica_telefono: response.data.clinica_telefono || "",
        clinica_email: response.data.clinica_email || "",
        logo_base64: response.data.logo_base64 || null,
        firma_base64: response.data.firma_base64 || null,
        encabezado_texto: response.data.encabezado_texto || "",
        pie_pagina_texto: response.data.pie_pagina_texto || ""
      });
    } catch (error) {
      toast.error(isUsHost ? "Unable to load profile" : "Error al cargar perfil");
    } finally {
      setLoading(false);
    }
  }, [isUsHost, user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleImageUpload = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(isUsHost ? "Image must be smaller than 2MB" : "La imagen debe ser menor a 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile({ ...profile, [field]: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDemoUser) { toast.error("El demo es de solo lectura. Crea tu cuenta para personalizar tu perfil."); return; }
    setSaving(true);
    try {
      await updateProfile(profile);
      toast.success(isUsHost ? "Profile updated successfully" : "Perfil actualizado correctamente");
    } catch (error) {
      toast.error(isUsHost ? "Unable to save profile" : "Error al guardar perfil");
    } finally {
      setSaving(false);
    }
  };

  const isProfileComplete = profile.nombre_completo && profile.clinica_nombre && profile.numero_colegiado;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (isDemoUser) { toast.error("El demo es de solo lectura. Crea tu cuenta para cambiar la contraseña."); return; }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(isUsHost ? "New password and confirmation do not match" : "La nueva contraseña y la confirmación no coinciden");
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error(isUsHost ? "New password must be at least 6 characters" : "La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    setChangingPassword(true);
    try {
      await changePassword(passwordForm.current_password, passwordForm.new_password);
      toast.success(isUsHost ? "Password updated successfully" : "Contraseña actualizada correctamente");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      const detail = err.response?.data?.detail || (isUsHost ? "Unable to change password" : "Error al cambiar la contraseña");
      toast.error(typeof detail === "string" ? detail : (isUsHost ? "Unable to change password" : "Error al cambiar la contraseña"));
    } finally {
      setChangingPassword(false);
    }
  };

  useAuroraTour(TOUR_CONFIG.personalizacion.key, TOUR_CONFIG.personalizacion.steps, {
    enabled: !loading && !isUsHost,
    delay: 850,
  });

  const handleRestartTours = () => {
    clearAllAuroraTours();
    toast.success(isUsHost ? "Getting started guide reset." : "Guía de inicio reiniciada.");
    window.location.href = "/dashboard";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">{isUsHost ? "Practice Settings" : "Configuración"}</h1>
          <p className="text-slate-500 mt-1">{isUsHost ? "Clinician profile and practice information" : "Perfil del médico y datos de la clínica"}</p>
        </div>
        <Link to="/configuracion/billing">
          <Button variant="outline" size="sm" className="border-slate-200">
            <CreditCard className="h-4 w-4 mr-2" />
            {isUsHost ? "Subscription & Billing" : "Facturación"}
          </Button>
        </Link>
      </div>

      {/* Account & Billing Section */}
      {user && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-slate-600" />
              {isUsHost ? "Account and subscription" : "Cuenta y suscripción"}
            </CardTitle>
            <CardDescription>{isUsHost ? "Current Aurora Geriatric account status" : "Estado de tu cuenta Aurora Geriátrica"}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-slate-500">{isUsHost ? "Clinician" : "Médico"}</p>
                <p className="font-medium text-slate-800">{user.nombre || "—"}</p>
              </div>
              <div>
                <p className="text-slate-500">Email</p>
                <p className="font-medium text-slate-800">{user.email || "—"}</p>
              </div>
              <div>
                <p className="text-slate-500">{isUsHost ? "Account type" : "Tipo de cuenta"}</p>
                <p className="font-medium text-slate-800 capitalize">{user.account_type || "paid"}</p>
              </div>
              <div>
                <p className="text-slate-500">{isUsHost ? "Subscription status" : "Estado suscripción"}</p>
                <p className="font-medium text-slate-800">
                  {user.is_billing_exempt ? (
                    <span className="text-emerald-600">{isUsHost ? "Exempt (founder/admin)" : "Exento (fundador/admin)"}</span>
                  ) : (
                    <>
                      {user.subscription_status === "active" && (isUsHost ? "Active" : "Activa")}
                      {user.subscription_status === "past_due" && (isUsHost ? "Past due" : "Pago pendiente")}
                      {user.subscription_status === "canceled" && (isUsHost ? "Canceled" : "Cancelada")}
                      {user.subscription_status === "trial" && (isUsHost ? "Free trial" : "Prueba gratuita")}
                      {!["active", "past_due", "canceled", "trial"].includes(user.subscription_status) && (user.subscription_status || "—")}
                    </>
                  )}
                </p>
              </div>
              {user.subscription_status === "active" && user.current_period_end && (
                <div>
                  <p className="text-slate-500">{isUsHost ? "Next billing date" : "Próxima facturación"}</p>
                  <p className="font-medium text-slate-800">
                    {new Date(user.current_period_end).toLocaleDateString(isUsHost ? "en-US" : "es-ES")}
                  </p>
                </div>
              )}
              {user.trial_ends_at && (
                <div>
                  <p className="text-slate-500">{isUsHost ? "Trial ends" : "Fin de prueba"}</p>
                  <p className="font-medium text-slate-800">
                    {new Date(user.trial_ends_at).toLocaleDateString(isUsHost ? "en-US" : "es-ES")}
                    {user.days_remaining_in_trial != null && (
                      <span className="ml-2 text-xs text-slate-500">
                        {isUsHost ? `(${user.days_remaining_in_trial} days remaining)` : `(${user.days_remaining_in_trial} días restantes)`}
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
            {user.is_billing_exempt && (
              <p className="text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-1 inline-block">
                {isUsHost
                  ? "Founder/admin account - no billing restrictions"
                  : "Cuenta fundador/admin — sin restricciones de facturación"}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cambiar contraseña */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-slate-600" />
            {isUsHost ? "Change password" : "Cambiar contraseña"}
          </CardTitle>
          <CardDescription>{isUsHost ? "Update your sign-in password. Your current password is required." : "Actualice su contraseña de acceso. Requiere su contraseña actual."}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="current_password">{isUsHost ? "Current password" : "Contraseña actual"}</Label>
              <Input
                id="current_password"
                type="password"
                autoComplete="current-password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm((p) => ({ ...p, current_password: e.target.value }))}
                placeholder="••••••••"
                required
                disabled={isDemoUser}
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">{isUsHost ? "New password" : "Nueva contraseña"}</Label>
              <Input
                id="new_password"
                type="password"
                autoComplete="new-password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm((p) => ({ ...p, new_password: e.target.value }))}
                placeholder={isUsHost ? "Minimum 6 characters" : "Mínimo 6 caracteres"}
                required
                minLength={6}
                disabled={isDemoUser}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">{isUsHost ? "Confirm new password" : "Confirmar nueva contraseña"}</Label>
              <Input
                id="confirm_password"
                type="password"
                autoComplete="new-password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm((p) => ({ ...p, confirm_password: e.target.value }))}
                placeholder={isUsHost ? "Re-enter new password" : "Repita la nueva contraseña"}
                required
                minLength={6}
                disabled={isDemoUser}
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" disabled={changingPassword} data-testid="change-password-submit">
              {changingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isUsHost ? "Update password" : "Actualizar contraseña"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {!isProfileComplete && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-amber-800 font-medium">
            {isUsHost
              ? "⚠️ Complete your profile to generate prescriptions and clinical documents."
              : "⚠️ Complete su perfil para poder generar recetas y documentos clínicos."}
          </p>
          <p className="text-amber-600 text-sm mt-1">
            {isUsHost
              ? "Required fields: Full name, Practice name, Medical license number."
              : "Campos requeridos: Nombre completo, Nombre de la clínica, Número de colegiado."}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Doctor Info */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {isUsHost ? "Clinician Information" : "Datos del Médico"}
            </CardTitle>
            <CardDescription>{isUsHost ? "Information displayed on generated documents" : "Información que aparecerá en los documentos generados"}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">{isUsHost ? "Full name *" : "Nombre completo *"}</Label>
                <Input
                  id="nombre"
                  value={profile.nombre_completo}
                  onChange={(e) => setProfile({ ...profile, nombre_completo: e.target.value })}
                  placeholder={isUsHost ? "Dr. Jane Smith" : "Dr. Juan Pérez"}
                  required
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="especialidad">{isUsHost ? "Specialty" : "Especialidad"}</Label>
                <Input
                  id="especialidad"
                  value={profile.especialidad}
                  onChange={(e) => setProfile({ ...profile, especialidad: e.target.value })}
                  placeholder={isUsHost ? "Geriatrics" : "Geriatría"}
                  data-testid="profile-specialty-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="colegiado">{isUsHost ? "Medical license number *" : "Número de colegiado *"}</Label>
                <Input
                  id="colegiado"
                  value={profile.numero_colegiado}
                  onChange={(e) => setProfile({ ...profile, numero_colegiado: e.target.value })}
                  placeholder="COL-12345"
                  required
                  data-testid="profile-license-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinic Info */}
        <Card className="border-slate-200" data-aurora-tour="personalizacion-clinica">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              {isUsHost ? "Practice Information" : "Datos de la Clínica"}
            </CardTitle>
            <CardDescription>{isUsHost ? "Letterhead and practice details" : "Información del membrete"}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="relative">
                  <DemoTooltip message="Personaliza Aurora con el nombre, logo y firma de tu clínica. Aparecerán automáticamente en todos los informes PDF que generes." position="bottom" />
                </div>
                <Label htmlFor="clinica">{isUsHost ? "Practice name *" : "Nombre de la clínica *"}</Label>
                <Input
                  id="clinica"
                  value={profile.clinica_nombre}
                  onChange={(e) => setProfile({ ...profile, clinica_nombre: e.target.value })}
                  placeholder={isUsHost ? "Aurora Geriatric Clinic" : "Clínica Geriátrica"}
                  required
                  data-testid="profile-clinic-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ciudad">{isUsHost ? "City" : "Ciudad"}</Label>
                <Input
                  id="ciudad"
                  value={profile.clinica_ciudad}
                  onChange={(e) => setProfile({ ...profile, clinica_ciudad: e.target.value })}
                  placeholder={isUsHost ? "City" : "Ciudad"}
                  data-testid="profile-clinic-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">{isUsHost ? "Phone" : "Teléfono"}</Label>
                <Input
                  id="telefono"
                  value={profile.clinica_telefono}
                  onChange={(e) => setProfile({ ...profile, clinica_telefono: e.target.value })}
                  placeholder="555-0100"
                  data-testid="profile-clinic-phone-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="direccion">{isUsHost ? "Address" : "Dirección"}</Label>
                <Input
                  id="direccion"
                  value={profile.clinica_direccion}
                  onChange={(e) => setProfile({ ...profile, clinica_direccion: e.target.value })}
                  placeholder={isUsHost ? "123 Main St, City" : "Av. Principal 123, Ciudad"}
                  data-testid="profile-clinic-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{isUsHost ? "Contact email" : "Email de contacto"}</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.clinica_email}
                  onChange={(e) => setProfile({ ...profile, clinica_email: e.target.value })}
                  placeholder="contacto@clinica.com"
                  data-testid="profile-clinic-email-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Images */}
        <Card className="border-slate-200" data-aurora-tour="personalizacion-logo">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {isUsHost ? "Logo and Signature" : "Logo y Firma"}
            </CardTitle>
            <CardDescription>{isUsHost ? "Images used on generated documents" : "Imágenes para los documentos generados"}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo */}
              <div className="space-y-3">
                <Label>{isUsHost ? "Practice logo" : "Logo de la clínica"}</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                  {profile.logo_base64 ? (
                    <div className="space-y-2">
                      <img
                        src={profile.logo_base64}
                        alt="Logo"
                        className="max-h-24 mx-auto object-contain"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setProfile({ ...profile, logo_base64: null })}
                        className="text-red-600"
                      >
                        {isUsHost ? "Remove" : "Eliminar"}
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">{isUsHost ? "Click to upload logo" : "Clic para subir logo"}</p>
                      <p className="text-xs text-slate-400">{isUsHost ? "PNG or JPG up to 2MB" : "PNG, JPG hasta 2MB"}</p>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, "logo_base64")}
                        data-testid="logo-upload-input"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Signature */}
              <div className="space-y-3">
                <Label>{isUsHost ? "Digital signature" : "Firma digital"}</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                  {profile.firma_base64 ? (
                    <div className="space-y-2">
                      <img
                        src={profile.firma_base64}
                        alt="Firma"
                        className="max-h-24 mx-auto object-contain"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setProfile({ ...profile, firma_base64: null })}
                        className="text-red-600"
                      >
                        {isUsHost ? "Remove" : "Eliminar"}
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">{isUsHost ? "Click to upload signature" : "Clic para subir firma"}</p>
                      <p className="text-xs text-slate-400">{isUsHost ? "PNG or JPG up to 2MB" : "PNG, JPG hasta 2MB"}</p>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, "firma_base64")}
                        data-testid="signature-upload-input"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custom Texts */}
        <Card className="border-slate-200" data-aurora-tour="personalizacion-frases">
          <CardHeader>
            <CardTitle>{isUsHost ? "Custom Text" : "Textos Personalizados"}</CardTitle>
            <CardDescription>{isUsHost ? "Additional text for generated documents" : "Texto adicional para los documentos"}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="encabezado">{isUsHost ? "Header text (optional)" : "Texto de encabezado (opcional)"}</Label>
              <Textarea
                id="encabezado"
                value={profile.encabezado_texto}
                onChange={(e) => setProfile({ ...profile, encabezado_texto: e.target.value })}
                placeholder={isUsHost ? "Text shown below the letterhead..." : "Texto que aparecerá debajo del membrete..."}
                rows={2}
                data-testid="profile-header-text"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pie">{isUsHost ? "Footer text (optional)" : "Texto de pie de página (opcional)"}</Label>
              <Textarea
                id="pie"
                value={profile.pie_pagina_texto}
                onChange={(e) => setProfile({ ...profile, pie_pagina_texto: e.target.value })}
                placeholder={isUsHost ? "Office hours, legal notice, etc..." : "Horarios de atención, aviso legal, etc..."}
                rows={2}
                data-testid="profile-footer-text"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        {isDemoUser && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            ⚠️ Estás en modo demo — la configuración no se puede modificar. Crea tu cuenta para personalizar Aurora con los datos de tu clínica.
          </div>
        )}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            {isUsHost ? "Cancel" : "Cancelar"}
          </Button>
          <Button
            type="submit"
            className="bg-slate-800 hover:bg-slate-700"
            disabled={saving || isDemoUser}
            data-testid="save-profile-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isUsHost ? "Saving..." : "Guardando..."}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isUsHost ? "Save changes" : "Guardar Perfil"}
              </>
            )}
          </Button>
        </div>
      </form>

      {!isUsHost && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base text-slate-800">Guía de inicio</CardTitle>
            <CardDescription>Vuelve a ver los recorridos interactivos de cada sección de Aurora.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              variant="outline"
              className="border-sky-200 text-sky-700 hover:bg-sky-50"
              onClick={handleRestartTours}
            >
              Reiniciar guía de inicio →
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
